import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  recurringExpenseService, 
  categoryService, 
  accountService
} from '../../../apiServices';
import { 
  RecurringExpense, 
  Category, 
  Account, 
  CreateRecurringExpenseRequest,
  CreateCategoryRequest 
} from '../../../apiServices/types';
import { formatCurrency, mapRecurrenceTypeFromBackend, mapRecurrenceTypeToBackend, ValidationConstants, ValidationUtils } from '../../../utils';
import { useNotification } from '../../../contexts/NotificationContext';
import { useRefresh } from '../../../contexts/RefreshContext';
import './RecurringPage.css';
import { Modal, GlassSelect, ConfirmationModal } from '../../UI';

const RecurringPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [recurringExpenses, setRecurringExpenses] = useState<RecurringExpense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [, setError] = useState<string>('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string>('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedRecurrenceType, setSelectedRecurrenceType] = useState<string>('2');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedDayOfMonth, setSelectedDayOfMonth] = useState<string>('');
  const [isDeletingExpense, setIsDeletingExpense] = useState<Record<string, boolean>>({});
  const [isCreatingReceipt, setIsCreatingReceipt] = useState<Record<string, boolean>>({});
  const [showConfirmClose, setShowConfirmClose] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string>('');
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Завантажуємо категорії та акаунти коли відкривається модалка
  useEffect(() => {
    if (showModal) {
      loadCategoriesInModal();
      loadAccountsInModal();
    }
  }, [showModal]);

  // Встановлюємо перший акаунт за замовчуванням після завантаження
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId && showModal) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId, showModal]);

  const loadInitialData = async (): Promise<void> => {
    try {
      setLoading(true);
      await loadRecurringExpenses();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriesInModal = async (): Promise<void> => {
    try {
      setLoadingCategories(true);
      await loadCategories();
    } catch (error) {
      showNotification('Помилка завантаження категорій', 'error');
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadAccountsInModal = async (): Promise<void> => {
    try {
      setLoadingAccounts(true);
      await loadAccounts();
    } catch (error) {
      showNotification('Помилка завантаження рахунків', 'error');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadRecurringExpenses = async (): Promise<void> => {
    try {
      const data = await recurringExpenseService.getAll();
      if (data.length > 0) {
      }
      setRecurringExpenses(data);
    } catch (error) {
      setError('Помилка завантаження регулярних витрат: ' + (error as Error).message);
    }
  };

  const loadCategories = async (): Promise<void> => {
    try {
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
    }
  };

  const loadAccounts = async (): Promise<void> => {
    try {
      const data = await accountService.getAll();
      setAccounts(data);
      
      if (data.length > 0 && !selectedAccountId) {
        setSelectedAccountId(data[0].id);
      }
    } catch (error) {
    }
  };

  const handleRefresh = useCallback(async () => {
    await loadRecurringExpenses();
  }, []);

  useRefresh(handleRefresh);

  const showCreateRecurringModal = (): void => {
    setEditingExpenseId('');
    setSelectedCategoryId('');
    setSelectedRecurrenceType('2'); 
    setSelectedAccountId('');
    setSelectedDayOfMonth('');
    setFormErrors({});
    setShowModal(true);
  };

  const hideRecurringModal = (): void => {
    setShowModal(false);
    setEditingExpenseId('');
    setSelectedCategoryId('');
    setSelectedRecurrenceType('2'); 
    setSelectedDayOfMonth('');
    setFormErrors({});
    setShowConfirmClose(false);
    setLoadingCategories(false);
    setLoadingAccounts(false);
  };

  const handleModalClose = (): void => {
    // Перевіряємо чи є незбережені зміни
    const form = document.getElementById('recurring-form') as HTMLFormElement;
    const formData = form ? new FormData(form) : null;
    const recurringName = formData?.get('recurring-name') as string;
    const recurringAmount = formData?.get('recurring-amount') as string;
    
    const hasChanges = recurringName || recurringAmount || selectedCategoryId || selectedAccountId;
    
    if (hasChanges) {
      setShowConfirmClose(true);
    } else {
      hideRecurringModal();
    }
  };

  const handleConfirmClose = (): void => {
    hideRecurringModal();
  };

  const handleCancelClose = (): void => {
    setShowConfirmClose(false);
  };

  const handleCreateCategory = async (categoryName: string): Promise<void> => {
    try {
      if (categoryName.length < ValidationConstants.Category.NameMinLength) {
        setFormErrors({ general: `Назва категорії має бути не менше ${ValidationConstants.Category.NameMinLength} символів` });
        showNotification(`Назва категорії має бути не менше ${ValidationConstants.Category.NameMinLength} символів`, 'error');
        return;
      }
      if (categoryName.length > ValidationConstants.Category.NameMaxLength) {
        setFormErrors({ general: `Назва категорії не може перевищувати ${ValidationConstants.Category.NameMaxLength} символів` });
        showNotification(`Назва категорії не може перевищувати ${ValidationConstants.Category.NameMaxLength} символів`, 'error');
        return;
      }
      if (ValidationUtils.containsForbiddenChars(categoryName)) {
        setFormErrors({ general: 'Назва категорії містить заборонені символи (<, >, ", &, \')' });
        showNotification('Назва категорії містить заборонені символи', 'error');
        return;
      }

      const existingCategory = categories.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase());
      if (existingCategory) {
        setSelectedCategoryId(existingCategory.id);
        return;
      }

      const newCategoryRequest: CreateCategoryRequest = { 
        name: categoryName.trim()
      };
      const newCategory = await categoryService.create(newCategoryRequest);
      
      setCategories(prev => [...prev, newCategory]);
      
      setSelectedCategoryId(newCategory.id);
      showNotification('Категорію успішно створено', 'success');
    } catch (error: any) {
      setFormErrors({ general: error.message || 'Помилка створення категорії' });
      showNotification('Помилка створення категорії', 'error');
    }
  };

  const saveRecurringExpense = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    
    let categoryId = selectedCategoryId;

    if (!categoryId) {
      setFormErrors({ general: 'Оберіть категорію' });
      showNotification('Оберіть категорію', 'error');
      return;
    }

    const expenseData: CreateRecurringExpenseRequest = {
      name: formData.get('recurring-name') as string,
      amount: parseFloat(formData.get('recurring-amount') as string),
      categoryId: categoryId,
      accountId: selectedAccountId,
      recurrenceType: mapRecurrenceTypeToBackend(selectedRecurrenceType),
      dayOfMonth: selectedRecurrenceType === '2' && selectedDayOfMonth ? parseInt(selectedDayOfMonth) : undefined,
      dayOfWeek: selectedRecurrenceType === '1' && selectedDayOfMonth ? parseInt(selectedDayOfMonth) : undefined
    };

    setIsLoading(true);
    setError('');
    setFormErrors({});

    try {
      if (editingExpenseId) {
        await recurringExpenseService.update(editingExpenseId, expenseData);
      } else {
        await recurringExpenseService.create(expenseData);
      }

      hideRecurringModal();
      await loadRecurringExpenses();
      showNotification(
        editingExpenseId ? 'Шаблон витрати успішно оновлено' : 'Шаблон витрати успішно додано',
        'success'
      );
    } catch (error: any) {
      setFormErrors({
        general: error.message || 'Помилка збереження шаблону витрати'
      });
      showNotification('Помилка збереження шаблону витрати', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const editRecurringExpense = (expenseId: string): void => {
    const expense = recurringExpenses.find(e => e.id === expenseId);
    if (!expense) return;


    setEditingExpenseId(expenseId);
    setSelectedAccountId(expense.accountId);
    setFormErrors({});
    
    const recurrenceType = expense.recurrenceType;
    if (recurrenceType !== undefined && recurrenceType !== null) {
      const frontendRecurrenceType = mapRecurrenceTypeFromBackend(recurrenceType);
      setSelectedRecurrenceType(frontendRecurrenceType);
      
      // Set day of month or day of week based on recurrence type
      if (frontendRecurrenceType === '2' && expense.dayOfMonth) {
        setSelectedDayOfMonth(expense.dayOfMonth.toString());
      } else if (frontendRecurrenceType === '1' && expense.dayOfWeek) {
        setSelectedDayOfMonth(expense.dayOfWeek.toString());
      } else {
        setSelectedDayOfMonth('');
      }
    } else {
      setSelectedRecurrenceType('2');
      setSelectedDayOfMonth('');
    }
    
    setSelectedCategoryId(expense.categoryId);
    setShowModal(true);
  };

  const deleteRecurringExpense = (expenseId: string): void => {
    if (isDeletingExpense[expenseId]) return;
    setShowConfirmDelete(expenseId);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    const expenseId = showConfirmDelete;
    if (!expenseId) return;
    
    setIsDeletingExpense(prev => ({ ...prev, [expenseId]: true }));
    
    try {
      await recurringExpenseService.delete(expenseId);
      await loadRecurringExpenses();
      showNotification('Шаблон витрати успішно видалено', 'success');
    } catch (error: any) {
      showNotification(error.message || 'Помилка видалення шаблону витрати', 'error');
    } finally {
      setIsDeletingExpense(prev => ({ ...prev, [expenseId]: false }));
      setShowConfirmDelete('');
    }
  };

  const handleCancelDelete = (): void => {
    setShowConfirmDelete('');
  };

  const createReceiptFromRecurring = async (expenseId: string): Promise<void> => {
    if (isCreatingReceipt[expenseId]) return;
    
    setIsCreatingReceipt(prev => ({ ...prev, [expenseId]: true }));
    
    try {
      await recurringExpenseService.createReceipt(expenseId);
      await loadRecurringExpenses();
      showNotification('Чек успішно створено з шаблону', 'success');
    } catch (error: any) {
      showNotification(error.message || 'Помилка створення чека', 'error');
    } finally {
      setIsCreatingReceipt(prev => ({ ...prev, [expenseId]: false }));
    }
  };


  const getRecurrenceTypeLabel = (recurrenceType: string | undefined): string => {
    if (!recurrenceType) return 'Щомісяця';
    
    // Якщо це вже числовий рядок "1", "2", "3", використовуємо його безпосередньо
    if (recurrenceType === '1' || recurrenceType === '2' || recurrenceType === '3') {
      switch (recurrenceType) {
        case '1': return 'Щотижня';
        case '2': return 'Щомісяця';
        case '3': return 'Щороку';
        default: return 'Щомісяця';
      }
    }
    
    // Інакше конвертуємо через mapRecurrenceTypeFromBackend
    const frontendType = mapRecurrenceTypeFromBackend(recurrenceType);
    switch (frontendType) {
      case '1': return 'Щотижня';
      case '2': return 'Щомісяця';
      case '3': return 'Щороку';
      default: return 'Щомісяця';
    }
  };

  const getRecurrenceDescription = (expense: RecurringExpense): string => {
    const typeLabel = getRecurrenceTypeLabel(expense.recurrenceType);
    
    if (expense.recurrenceType === '2' || expense.recurrenceType === 'Monthly') {
      if (expense.dayOfMonth) {
        const dayLabel = `${expense.dayOfMonth}-го`;
        return `${typeLabel} ${dayLabel} дня`;
      }
    }
    
    return typeLabel;
  };

  const recurringTotals = useMemo(() => {
    const isMonthlyExpense = (expense: RecurringExpense): boolean => {
      const mappedType = mapRecurrenceTypeFromBackend(expense.recurrenceType);
      return mappedType === '2';
    };

    const monthlyExpenses = recurringExpenses.filter(isMonthlyExpense);
    const activeMonthlyExpenses = monthlyExpenses.filter(expense => expense.isActive);

    const totalMonthlyFixed = activeMonthlyExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const averageMonthlyFixed = activeMonthlyExpenses.length > 0
      ? totalMonthlyFixed / activeMonthlyExpenses.length
      : 0;

    return {
      totalTemplates: recurringExpenses.length,
      activeTemplates: recurringExpenses.filter(expense => expense.isActive).length,
      monthlyTemplates: monthlyExpenses.length,
      activeMonthlyTemplates: activeMonthlyExpenses.length,
      totalMonthlyFixed,
      averageMonthlyFixed
    };
  }, [recurringExpenses]);

  if (loading) {
    return (
      <div className="recurring-page">
        <div className="card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження шаблонів...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recurring-page">
      <div className="recurring-page-header">
        <div className="recurring-page-header-content">
          <h2 className="recurring-page-header-title">Шаблони витрат</h2>
          <p className="recurring-page-header-subtitle">Зберігайте шаблони для швидкого створення чеків</p>
        </div>
        <button className="recurring-page-header-button" onClick={showCreateRecurringModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M2 12h20"></path>
          </svg>
          <span>Створити</span>
        </button>
      </div>

      <div className="recurring-totals-card card">
        <div className="recurring-totals-header">
          <h3 className="recurring-totals-title">Огляд щомісячних сталих витрат</h3>
        </div>
        <div className="recurring-totals-grid">
          <div className="recurring-totals-stat recurring-totals-stat-highlight">
            <div className="recurring-totals-stat-label">Сума в місяць</div>
            <div className="recurring-totals-stat-value">
              {formatCurrency(recurringTotals.totalMonthlyFixed)}
            </div>
          </div>

          <div className="recurring-totals-stat recurring-totals-stat-active">
            <div className="recurring-totals-stat-label">Кількість платежів</div>
            <div className="recurring-totals-stat-value">
              {recurringTotals.totalTemplates}
            </div>
          </div>
        </div>
      </div>

      <div className="recurring-list">
        {recurringExpenses.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 4v6h6"></path>
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
              </svg>
            </div>
            <div className="empty-title">Немає шаблонів витрат</div>
            <div className="empty-text">Створіть свій перший шаблон для швидкого створення чеків</div>
          </div>
        ) : (
          recurringExpenses.map(expense => (
            <div key={expense.id} className={`recurring-card ${!expense.isActive ? 'recurring-card-inactive' : ''}`}>
              <div className="recurring-card-header">
                <div className="recurring-card-title-section">
                  <div className="recurring-title-content">
                    <h3 className="recurring-card-title">{expense.name}</h3>
                    <div className="recurring-card-meta">
                      <span className="recurring-category-name">{expense.categoryName}</span>
                      <span className="recurring-meta-separator">•</span>
                      <span className="recurring-account-name">{expense.accountName}</span>
                      <span className="recurring-meta-separator">•</span>
                      <span className="recurring-recurrence-type">{getRecurrenceDescription(expense)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="recurring-card-actions">
                  <button 
                    className="recurring-action-icon recurring-action-icon-primary" 
                    onClick={() => createReceiptFromRecurring(expense.id)} 
                    disabled={isCreatingReceipt[expense.id]}
                    title={isCreatingReceipt[expense.id] ? "Створення..." : "Створити чек з шаблону"}
                  >
                    {isCreatingReceipt[expense.id] ? (
                      <div className="spinner-small"></div>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2v20M2 12h20"></path>
                      </svg>
                    )}
                  </button>
                  <button 
                    className="recurring-action-icon" 
                    onClick={() => editRecurringExpense(expense.id)} 
                    title="Редагувати"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button 
                    className="recurring-action-icon recurring-action-icon-danger" 
                    onClick={() => deleteRecurringExpense(expense.id)} 
                    disabled={isDeletingExpense[expense.id]}
                    title={isDeletingExpense[expense.id] ? "Видалення..." : "Видалити"}
                  >
                    {isDeletingExpense[expense.id] ? (
                      <div className="spinner-small"></div>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="recurring-card-body">
                <div className="recurring-amount-display">
                  <div className="recurring-amount-label">Сума</div>
                  <div className="recurring-amount-value">{formatCurrency(expense.amount)}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={handleModalClose}
        title={editingExpenseId ? 'Редагувати шаблон витрати' : 'Додати шаблон витрати'}
        size="md"
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={handleModalClose}>
              Скасувати
            </button>
            <button
              type="submit"
              form="recurring-form"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Збереження...' : (editingExpenseId ? 'Оновити шаблон' : 'Додати шаблон')}
            </button>
          </>
        )}
      >
        {(() => {
          const current = editingExpenseId ? recurringExpenses.find(e => e.id === editingExpenseId) : undefined;
          return (
            <form id="recurring-form" onSubmit={saveRecurringExpense}>
          <div className="form-group">
            <label className="form-label" htmlFor="recurring-name">Назва шаблону</label>
            <input
              type="text"
              id="recurring-name"
              name="recurring-name"
              className="form-input"
              required
              minLength={ValidationConstants.RecurringExpense.NameMinLength}
              maxLength={ValidationConstants.RecurringExpense.NameMaxLength}
              placeholder="Наприклад: Комунальні послуги"
              defaultValue={current ? current.name : ''}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="recurring-amount">Сума</label>
            <input
              type="number"
              id="recurring-amount"
              name="recurring-amount"
              className="form-input"
              required
              min="0.01"
              max={ValidationConstants.RecurringExpense.MaxAmount}
              step="0.01"
              placeholder="0.00"
              defaultValue={current ? String(current.amount) : ''}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Категорія</label>
            <GlassSelect
              options={[
                ...categories.map(cat => ({
                  value: cat.id,
                  label: cat.name
                }))
              ]}
              value={selectedCategoryId}
              onChange={setSelectedCategoryId}
              placeholder="Оберіть категорію"
              searchable
              allowCustomValue={true}
              onCustomValue={handleCreateCategory}
              disabled={loadingCategories}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Рахунок</label>
            <GlassSelect
              options={accounts.map(account => ({
                value: account.id,
                label: account.name
              }))}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
              placeholder="Оберіть рахунок"
              disabled={loadingAccounts}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Тип повторення</label>
            <GlassSelect
              options={[
                { value: '1', label: 'Щотижня' },
                { value: '2', label: 'Щомісяця' },
                { value: '3', label: 'Щороку' }
              ]}
              value={selectedRecurrenceType}
              onChange={setSelectedRecurrenceType}
              placeholder="Оберіть тип повторення"
            />
          </div>

          {selectedRecurrenceType === '2' && (
            <div className="form-group">
              <label className="form-label">День місяця для оплати</label>
              <GlassSelect
                options={[...Array(31)].map((_, i) => ({
                  value: (i + 1).toString(),
                  label: (i + 1).toString()
                }))}
                value={selectedDayOfMonth}
                onChange={setSelectedDayOfMonth}
                placeholder="Оберіть день місяця"
                searchable
              />
            </div>
          )}

          {formErrors.general && (
            <div className="error-message general-error">
              {formErrors.general}
            </div>
          )}
          </form>
          );
        })()}
      </Modal>

      <ConfirmationModal
        isOpen={showConfirmClose}
        title="Закрити форму?"
        message="У вас є незбережені зміни. Ви впевнені, що хочете закрити форму без збереження?"
        confirmText="Так"
        cancelText="Скасувати"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        variant="warning"
      />

      <ConfirmationModal
        isOpen={!!showConfirmDelete}
        title="Видалити шаблон?"
        message="Ви впевнені, що хочете видалити цей шаблон витрати? Цю дію неможливо скасувати."
        confirmText="Так"
        cancelText="Скасувати"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        variant="danger"
      />
    </div>
  );
};

export default RecurringPage;
